import { useCallback, useState, useEffect } from 'react';
import { performDeleteRequest } from '@atlassian/jira-fetch';
import type { IssueKey } from '@atlassian/jira-shared-types';

import { useIssueAttachmentsActions } from '../attachments-service';
import { useDialogStatus } from './context';

type State = {
	isDeleting: boolean;
	didDelete: boolean;
	error: Error | undefined;
};

export const initialState: State = { isDeleting: false, didDelete: false, error: undefined };

export const generateDeleteUrl = (baseUrl: string, issueKey: IssueKey) =>
	`${baseUrl}/rest/api/2/issue/${issueKey}/attachments`;

export const useDeleteAllService = (issueKey: IssueKey) => {
	const [isDialogOpen, { setDeleteAllDialogOpen }] = useDialogStatus();
	const [, { refreshAttachments }] = useIssueAttachmentsActions();
	const [state, setState] = useState<State>(initialState);

	const baseUrl = '';

	useEffect(() => {
		if (!isDialogOpen) {
			setState(initialState);
		}
	}, [isDialogOpen]);

	const makeDeleteAllRequest = useCallback(async () => {
		setState({ isDeleting: true, didDelete: false, error: undefined });

		try {
			await performDeleteRequest(generateDeleteUrl(baseUrl, issueKey));
			setDeleteAllDialogOpen(false);
			refreshAttachments(issueKey);
			setState({ isDeleting: false, didDelete: true, error: undefined });
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			setState({ isDeleting: false, didDelete: false, error: error as Error });
		}
	}, [baseUrl, issueKey, refreshAttachments, setDeleteAllDialogOpen]);

	return [state, makeDeleteAllRequest] as const;
};
